// app/(admin)/channels/page.tsx

import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import PageTitle from "../../../components/ui/PageTitle";
import Button from "../../../components/ui/Button";
import LoadingIndicator from "../../../components/ui/LoadingIndicator";
import ErrorMessage from "../../../components/ui/ErrorMessage";
import EmptyState from "../../../components/ui/EmptyState";
import ChannelListItem from "../../../components/admin/ChannelListItem";
import { getAdminChannels } from "../../../lib/apiClient";
import { Channel, PopulatedChannel } from "../../../types";
import { getErrorMessage } from "../../../lib/utils";
import toast from "react-hot-toast";
import { ROUTES } from "../../../lib/constants";

export default function AdminChannelsPage() {
  const [channels, setChannels] = useState<Channel[] | undefined | PopulatedChannel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Memoized fetch function
  const fetchChannels = useCallback(async () => {
    console.log("Fetching admin channels...");
    setIsLoading(true);
    setError(null);
    try {
      const data = await getAdminChannels(); // API returns channels owned by the admin
      setChannels(data);
      console.log(`Fetched ${data.length} channels.`);
    } catch (err) {
      const message = getErrorMessage(err);
      console.error("Failed to fetch channels:", message);
      setError(message);
      toast.error(`Error loading channels: ${message}`, {
        id: "fetch-channels-error",
      });
    } finally {
      setIsLoading(false);
    }
  }, []); // No dependencies needed

  // Initial fetch on mount
  useEffect(() => {
    fetchChannels();
  }, []);

  // --- Render Logic ---
  const renderContent = () => {
    if (isLoading) {
      return <LoadingIndicator text="Loading your channels..." />;
    }

    if (error) {
      return (
        <div className="text-center">
          <ErrorMessage title="Error Loading Channels" message={error} />
          <Button onClick={fetchChannels} className="mt-4">
            Retry
          </Button>
        </div>
      );
    }

    if (channels && channels.length === 0 || !channels) {
      return (
        <EmptyState
          message="You haven't added any channels yet."
        >
          <Link to={ROUTES.ADMIN_CHANNEL_NEW}>
            <Button variant="primary" size="md">
              + Add Your First Channel
            </Button>
          </Link>
        </EmptyState>
      );
    }

    // Display list of channels
    return (
      <div className="space-y-4">
        {channels && channels.map((channel, index) => (
          <ChannelListItem key={index} channel={channel} />
        ))}
      </div>
    );
  };

  return (
    <div>
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <PageTitle
          title="My Channels"
          subtitle="Manage your Telegram, Discord, and Slack channels."
        />
        {!isLoading && !error && (
          <Link to={ROUTES.ADMIN_CHANNEL_NEW} className="flex-shrink-0">
            <Button variant="primary" size="md">
              + Add Channel
            </Button>
          </Link>
        )}
      </div>

      {/* Content Area */}
      < div className="bg-dark-secondary p-4 md:p-6 rounded-lg shadow-md min-h-[200px] flex flex-col justify-center" >
        {renderContent()}
      </div >
    </div >
  );
}
